import { m, useReducedMotion } from 'motion/react'
import { cn } from '../../lib/cn.ts'
import { MARK_BARS, MARK_VIEW_BOX } from '../../lib/rally-mark-geometry.ts'
import { emissionAnimation, LOADER_SIZE, type LoaderGround, type LoaderSize, ROUTE_RULE_DURATION } from './emission.ts'

// #0c0b0a at 60% weight over the bone ground, resolved to a solid value: on a light ground the
// label is a lighter ink, never the dark ink at reduced opacity (DESIGN_LANGUAGE §1).
const BONE_LABEL_INK = '#6b6862'

export interface EmissionMarkProps {
  size: LoaderSize
  ink: string
  /** Override the size table's mark height without changing its motion channel — the boot
   * lockup (RAL-183) runs the lg loop at its own 84px lockup scale. */
  height?: number
}

// The mark on its own, no label and no layout, so a composition with its own furniture (the
// boot lockup) reuses the loop instead of restating it.
export function EmissionMark({ size, ink, height }: EmissionMarkProps) {
  const reduce = useReducedMotion()
  const markHeight = height ?? LOADER_SIZE[size].markHeight

  return (
    <svg
      viewBox={`0 0 ${MARK_VIEW_BOX.width} ${MARK_VIEW_BOX.height}`}
      width={(markHeight * MARK_VIEW_BOX.width) / MARK_VIEW_BOX.height}
      height={markHeight}
      fill="none"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
    >
      {MARK_BARS.map((bar, index) => (
        <m.rect
          key={bar.x}
          x={bar.x}
          y={(MARK_VIEW_BOX.height - bar.h) / 2}
          width={bar.w}
          height={bar.h}
          fill={bar.center ? 'var(--color-signal-oxide)' : ink}
          // Without both of these SVG scales about the viewBox origin and the mark collapses
          // into its top-left corner.
          style={{ transformOrigin: 'center', transformBox: 'fill-box' }}
          // Reduced motion means no looping animation at all, not a gentler one: the mark
          // renders static at full height and the label and layout are unchanged.
          {...(reduce ? {} : emissionAnimation(size, index))}
        />
      ))}
    </svg>
  )
}

export interface RallyLoaderProps {
  size?: LoaderSize
  ground?: LoaderGround
  /** What is being restored ("Loading workspace"), never "Please wait" — RAL-182 copy rules. */
  label: string
  /** lg only; ignored at sm/md. */
  wordmark?: boolean
  className?: string
}

export function RallyLoader({ size = 'md', ground = 'void', label, wordmark = false, className }: RallyLoaderProps) {
  const spec = LOADER_SIZE[size]
  const barInk = ground === 'bone' ? 'var(--color-ground-void)' : spec.ink
  const labelInk = ground === 'bone' ? BONE_LABEL_INK : 'var(--color-ink-meta)'
  return (
    <div
      role="status"
      className={cn(
        'flex',
        spec.layout === 'inline' ? 'flex-row items-center gap-2.5' : 'flex-col items-center gap-4',
        className
      )}
    >
      <span className={cn('flex items-center', wordmark && size === 'lg' && 'gap-4')}>
        <EmissionMark size={size} ink={barInk} />
        {wordmark && size === 'lg' ? (
          <span
            className="font-display text-[40px] font-extrabold uppercase leading-[0.9] tracking-tight [font-stretch:120%]"
            style={{ color: barInk }}
          >
            Rally
          </span>
        ) : null}
      </span>
      <span className={cn('font-mono uppercase tracking-[0.13em]', spec.labelClass)} style={{ color: labelInk }}>
        {label}
      </span>
    </div>
  )
}

export interface IndeterminateRuleProps {
  duration?: number
  className?: string
}

// A 24% oxide fill sweeping a 2px track. Symmetric in-out ease so it accelerates in and
// decelerates out of frame — it must never read as a determinate bar filling up.
export function IndeterminateRule({ duration = ROUTE_RULE_DURATION, className }: IndeterminateRuleProps) {
  const reduce = useReducedMotion()

  return (
    <div
      aria-hidden="true"
      className={cn('h-0.5 w-full overflow-hidden', className)}
      style={{ background: 'var(--color-track)' }}
    >
      <m.div
        className="h-full w-[24%]"
        style={{ background: 'var(--color-signal-oxide)' }}
        animate={reduce ? undefined : { x: ['-100%', '420%'] }}
        transition={reduce ? undefined : { duration, ease: [0.5, 0, 0.5, 1], repeat: Infinity }}
      />
    </div>
  )
}
